<template>
  <div class="m-2">
    <div v-if="!expand"
      class="hover:bg-sky-200 text-xs"
      @click="clickPreview">
      preview user config: {{ previewData }}
    </div>
    <div v-if="expand">
      <div class="flex flex-col bg-gray-300 rounded-md p-2 space-y-1">
        <div class="flex flex-row space-x-2 justify-start">
          <div class="px-3">
            User Config
          </div>
          <div class="italic text-gray-400"
            v-if="pendingChanges">
            Pending Changes
          </div>
          <div class="grow"></div>
          <Button class="bg-orange-200 rounded-sm text-sm px-1 text-gray-400"
            @click="toggleRawJson">
            {{ toggleRawJsonDisplay }}
          </Button>
          <Button class="bg-sky-200 rounded-sm text-sm px-1 text-gray-400"
            @click="closeExpand">
            __
          </Button>
        </div>
        <div
          v-if="!useRawJson">
          <div class="flex flex-col">
            <div class="mb-1 px-2 py-1 rounded-md"
              v-for="(item, index) in currMetadata"
              :key="index"
              :class="{
                'bg-gray-200': index % 2 == 0,
                'bg-sky-200': index % 2 == 1,
              }">
              <UserIdMetadataEntry
                v-if="item.type === 'userId'"
                :defaultValue="item"
                @valueChange="setConfigValue"
                @valueDelete="clearConfigValue"
                @markPending="markPendingItem"
                @deleteKeyValue="deleteKeyValue(index)"
              />
              <DistrictCode
                v-if="item.type === 'districtCode'"
                :defaultValue="item"
                @valueChange="setConfigValue"
                @valueDelete="clearConfigValue"
                @markPending="markPendingItem"
                @deleteKeyValue="deleteKeyValue(index)"
              />
              <VendorId
                v-if="item.type === 'vendorId'"
                :defaultValue="item"
                @valueChange="setConfigValue"
                @valueDelete="clearConfigValue"
                @markPending="markPendingItem"
                @deleteKeyValue="deleteKeyValue(index)"
              />
              <StudentIds
                v-if="item.type === 'studentIds'"
                :defaultValue="item"
                @valueChange="setConfigValue"
                @valueDelete="clearConfigValue"
                @markPending="markPendingItem"
                @deleteKeyValue="deleteKeyValue(index)"
              />
              <KeyValue
                v-if="item.type === 'keyValue'"
                :defaultValue="item"
                @keyValueChange="setConfigValue"
                @keyValueDelete="clearConfigValue"
                @markPending="markPendingItem"
                @deleteKeyValue="deleteKeyValue(index)"
              />
              <span class="bg-red-200 text-red-400"
                v-if="!METADATA_ENTRY_TYPES.includes(item.type)">
                Invalid Metadata type
              </span>
            </div>
          </div>
          <div class="flex flex-row space-x-2 justify-center">
            <span>
              Add Field
            </span>
            <AddMultiTypeButton
              @addField="addField"
            />
          </div>
        </div>
        <div
          v-else>
          <AppMetadataJson
            v-model="formValues"
            @markPending="markPendingJson"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'clone-deep'
import { isEmpty } from 'lodash'

import Button from '../../Button.vue'

import AddMultiTypeButton from './AddMultiTypeButton.vue'

import KeyValue from '../MetadataEntry/KeyValue.vue'
import UserIdMetadataEntry from '../MetadataEntry/UserIdMetadataEntry.vue'
import DistrictCode from '../MetadataEntry/DistrictCode.vue'
import VendorId from '../MetadataEntry/VendorId.vue'
import StudentIds from '../MetadataEntry/StudentIds.vue'

import AppMetadataJson from '../AppMetadataJson.vue'

export default {
  props: {
    auth0User: String,
    defaultValue: Object,
  },
  components: {
    Button,
    AddMultiTypeButton,
    AppMetadataJson,
    KeyValue,
    UserIdMetadataEntry,
    DistrictCode,
    VendorId,
    StudentIds,
  },
  data() {
    return {
      METADATA_ENTRY_TYPES: ['userId', 'districtCode', 'vendorId', 'studentIds', 'keyValue'],

      formValues: {},

      expand: false,
      pendingChanges: false,
      currMetadata: undefined,

      useRawJson: false,
    }
  },
  methods: {
    clickPreview() {
      this.expand = true
    },
    closeExpand() {
      this.expand = false
    },
    toggleRawJson() {
      this.useRawJson = !this.useRawJson
    },
    updateValue() {
      this.$emit('input', this.formValues)
    },
    setConfigValue(key, value) {
      if (this.formValues === undefined) {
        this.formValues = {}
      }

      this.$set(this.formValues, key, value)
    },
    clearConfigValue(key) {
      if (this.formValues === undefined) {
        this.formValues = {}
      }

      this.$delete(this.formValues, key)
    },
    addField(type) {
      // TODO: Update the special types to not overwrite the `formValues.key.input` nor `formValues.value.input`
      this.currMetadata.push({
        type: type,
        key: {
          current: undefined,
          input: undefined,
        },
        value: {
          current: undefined,
          input: undefined,
        },
      })
    },
    deleteKeyValue(index) {
      this.currMetadata.splice(index, 1)
    },
    markPendingItem(item, type) {
      this.$emit('markPending', this.auth0User, item, type)
    },
    markPendingJson() {
      this.$emit('markPending', this.auth0User)
    },
  },
  computed: {
    previewData() {
      if (!isEmpty(this.formValues)) {
        return JSON.stringify(this.formValues)
      }

      if (this.defaultValue === undefined) {
        return 'Empty Config'
      }
      else {
        return this.defaultValue
      }
    },
    displayData() {
      if (this.defaultValue === undefined) {
        return []
      }

      let retVal = []
      for (const key in this.defaultValue) {
        let type = undefined
        switch (key) {
          case 'user_id':
            type = 'userId'
            break
          case 'vendor_id':
            type = 'vendorId'
            break
          case 'student_ids':
            type = 'studentIds'
            break
          case 'district_code':
            type = 'districtCode'
            break
          default:
            if (this.defaultValue[key]) {
              type = 'keyValue'
            }
            else {
              continue
            }
            break
        }

        retVal.push({
          type: type,
          key: {
            current: key,
            input: key,
          },
          value: {
            current: this.defaultValue[key],
            input: this.defaultValue[key],
          },
        })
      }
      return retVal
    },
    toggleRawJsonDisplay() {
      if (this.useRawJson) {
        return 'Key-Value'
      }
      else {
        return 'Raw JSON'
      }
    }
  },
  mounted() {
    if (this.defaultValue !== undefined) {
      this.formValues = this.defaultValue
    }
    this.currMetadata = cloneDeep(this.displayData)
  },
  watch: {
    value(newValue) {
      this.formValues = newValue
    },
    formValues() {
      this.updateValue()
    },
    defaultValue(newValue) {
      if (newValue !== undefined) {
        this.formValues = newValue
      }
      this.currMetadata = cloneDeep(this.displayData)
    }
  }
}
</script>
